const {
  EXPERIENCE_COMPONENT_TYPES,
} = require('../../../../../config/analytics');
const { arrify } = require('../../common/utils/arrayUtils');
const {
  addClickWithoutDraggingEventListener,
  onDOMContentLoaded,
} = require('../../common/utils/domEvents');
const {
  createIntersectionObserver,
  createIntersectionObserverHandler,
} = require('../../common/utils/intersectionObserver');
const { watchMediaQuery } = require('../../common/utils/mediaQuery');
const { analyticsEventBus, ANALYTICS_EVENTS } = require('../events');

const componentPositionDataName = 'componentPosition';
const componentNameAttr = 'data-component-name';
const componentTypeAttr = 'data-component-type';
const defaultMainComponentsSelector = `.the-home-module > [${componentTypeAttr}]`;
const positionUtils = require('../ga4/utils/positionUtils');

/**
 * A home component is a direct child of the main region of a home. It is
 * wrapped by a tag with the class '.the-home-module' and it could have
 * subcomponents (example: images of an slider).
 *
 * @typedef {Element} HomeComponent
 */

/**
 * Get the main components function by breakpoint
 * @param {string} mainComponentsSelector main selector to use
 * @returns {function} Main components of the home.
 */
function makeGetComponents(mainComponentsSelector) {
  /**
   * @type {HomeComponent[]} Main components of the home for tablet/desktop.
   * Private variable to save queries to DOM caching the first query.
   */
  let componentsForDesktop;
  /**
   * @type {HomeComponent[]} Main components of the home for mobile.
   * Private variable to save queries to DOM caching the first query.
   */
  let componentsForMobile;

  let isMobile = false;
  watchMediaQuery({
    fnMatches: () => {
      isMobile = false;
    },
    fnNotMatches: () => {
      isMobile = true;
    },
    mediaQuery: '(min-width: 768px)',
  });

  const cacheComponents = () => {
    const allComponents = [
      ...document.querySelectorAll(mainComponentsSelector),
    ];
    componentsForDesktop = allComponents.filter(
      (elem) => elem.getAttribute('data-include-desktop') === 'true'
    );
    componentsForMobile = allComponents.filter(
      (elem) => elem.getAttribute('data-include-mobile') === 'true'
    );
  };

  cacheComponents();
  return () => (isMobile ? componentsForMobile : componentsForDesktop);
}
/**
 * Get the main components
 * @returns {HomeComponent[]} Main components of the home.
 */
let getComponents;

/**
 * Get the position (zero-based) of a main component of the home.
 * @param {HomeComponent|Element} component The component
 * @param {Element[]|NodeList} [siblings] The ordered list of the component and
 * its siblings. Must be set if the component passed is not a HomeComponent.
 * @returns {number} The zero-based position
 */
function getComponentPosition(component, siblings) {
  let orderedComponents;
  if (siblings) {
    orderedComponents = [...siblings];
  } else {
    orderedComponents = getComponents();
  }
  return orderedComponents?.indexOf(component) ?? -1;
}

/**
 * Get the position (zero-based) of a main component of the home by dataset.
 * @param {Element} component The component
 * @returns {number} The zero-based position
 */
const getDataComponentPosition = (component) =>
  parseInt(component?.dataset?.[componentPositionDataName] || -1, 10);

/**
 * Get a selector for querying components from the DOM of desired type/s.
 * @param {EXPERIENCE_COMPONENT_TYPES|EXPERIENCE_COMPONENT_TYPES[]} type The
 * type or the array of types looked for
 * @returns {string} The selector
 */
function getComponentSelectorForType(type) {
  return arrify(type)
    .map((t) => `[${componentTypeAttr}="${t}"]`)
    .join();
}

/**
 * Get the name of a component of the home
 * @param {Element} component The component
 * @returns {string} The name defined in PD
 */
function getComponentName(component) {
  return component?.getAttribute(componentNameAttr) ?? '';
}

/**
 * Get the type of a component of the home
 * @param {Element} component The component
 * @returns {EXPERIENCE_COMPONENT_TYPES|null} The type
 */
function getComponentType(component) {
  if (component?.querySelector('[href$="#searchable"]')) {
    return EXPERIENCE_COMPONENT_TYPES.topSearch;
  }

  return component?.getAttribute(componentTypeAttr) ?? null;
}

/**
 * Get the type position of a product in carousel
 * @param {Element} event The event
 * @returns {number} One-based position of the product been clicked
 */
function checkPositionProductCarousel(event) {
  const productContainerClicked = event.target.closest('article');
  const position = productContainerClicked.dataset.key.split('-')[1];
  const positionAsNumber = parseInt(position, 10);
  return positionUtils.oneBasedPosition(positionAsNumber);
}

/**
 *
 * @param {Event} event The click event handled
 * @param {HomeComponent} clickedComponent The parent home component
 * @param {EXPERIENCE_COMPONENT_TYPES|EXPERIENCE_COMPONENT_TYPES[]} typeInclusions
 * The types that children components can be included from. Defined in the JSON
 * of the parent HomeComponent.
 * @returns {number} Zero-based or One-based position as requested:
 * - One-based position for category featured products in carousel
 * - Zero-based position on other cases
 */
function getClickedDescendantPosition(event, clickedComponent, typeInclusions) {
  let clickedDescendantComponent;
  let allDescendantComponents;
  let position;
  if (
    typeInclusions.includes(
      EXPERIENCE_COMPONENT_TYPES.categoryFeaturedProducts
    ) &&
    event.target.closest('article')
  ) {
    position = checkPositionProductCarousel(event);
  } else {
    const descendantSelector = getComponentSelectorForType(typeInclusions);
    clickedDescendantComponent = event.target.closest(descendantSelector);
    allDescendantComponents =
      clickedComponent.querySelectorAll(descendantSelector);
    return getComponentPosition(
      clickedDescendantComponent,
      allDescendantComponents
    );
  }
  return position;
}

const getPage = (target) =>
  target?.closest('[data-page-id]')?.getAttribute('data-page-id');

const getPageType = (target) =>
  target?.closest('[data-page-type]')?.getAttribute('data-page-type') ??
  gaInfo.pageType;

const isDescendantOfModal = (element) => element.closest('.modal') !== null;

/**
 * Handle the event and publish the analytic event containing the needed data
 * @param {Event} evt The event of the click
 */
function handleComponentClick(evt) {
  // Avoid clicks from modals that are children of a main component (ex: Shop the look)
  if (isDescendantOfModal(evt.target)) return;

  const clickedComponent = evt.target.closest(defaultMainComponentsSelector);
  const name = getComponentName(clickedComponent);
  const position = getDataComponentPosition(clickedComponent);
  const type = getComponentType(clickedComponent);

  let typeInclusions;
  if (
    type === EXPERIENCE_COMPONENT_TYPES.slider ||
    type === EXPERIENCE_COMPONENT_TYPES.sliderAndText ||
    type === EXPERIENCE_COMPONENT_TYPES.layout3Columns
  ) {
    typeInclusions = EXPERIENCE_COMPONENT_TYPES.image;
  } else if (type === EXPERIENCE_COMPONENT_TYPES.categoryFeatured) {
    typeInclusions = [
      EXPERIENCE_COMPONENT_TYPES.freeText,
      EXPERIENCE_COMPONENT_TYPES.image,
      EXPERIENCE_COMPONENT_TYPES.categoryFeaturedProducts,
    ];
  } else if (type === EXPERIENCE_COMPONENT_TYPES.layoutColumns) {
    typeInclusions = [
      EXPERIENCE_COMPONENT_TYPES.freeText,
      EXPERIENCE_COMPONENT_TYPES.image,
    ];
  } else if (type === EXPERIENCE_COMPONENT_TYPES.oneColumnWithBackground) {
    typeInclusions = [
      EXPERIENCE_COMPONENT_TYPES.banner,
      EXPERIENCE_COMPONENT_TYPES.freeText,
      EXPERIENCE_COMPONENT_TYPES.image,
    ];
  } else if (type === EXPERIENCE_COMPONENT_TYPES.productCarousel) {
    typeInclusions = EXPERIENCE_COMPONENT_TYPES.product;
  } else if (type === EXPERIENCE_COMPONENT_TYPES.searchForm) {
    typeInclusions = EXPERIENCE_COMPONENT_TYPES.image;
  } else if (type === EXPERIENCE_COMPONENT_TYPES.shopTheLook) {
    typeInclusions = EXPERIENCE_COMPONENT_TYPES.look;
  }

  const clickedDescendantPosition = typeInclusions
    ? getClickedDescendantPosition(evt, clickedComponent, typeInclusions)
    : -1;

  const eventPayload = {
    name,
    page: getPage(clickedComponent),
    pageType: getPageType(clickedComponent),
    position,
    type,
  };

  if (type === EXPERIENCE_COMPONENT_TYPES.topSearch) {
    eventPayload.additionalParam = ['search_suggested', evt.target.innerText];
  }

  if (clickedDescendantPosition > -1) {
    eventPayload.clickedDescendantPosition = clickedDescendantPosition;
  }

  analyticsEventBus.publish(
    ANALYTICS_EVENTS.pageDesignerComponentClicked,
    eventPayload
  );
}

/**
 * Track the main components impressions via observer
 * @param {string} mainComponentsSelector main selector to use
 */
function trackImpressions(mainComponentsSelector) {
  createIntersectionObserver(
    mainComponentsSelector,
    createIntersectionObserverHandler({
      startCallback: ({ target }) => {
        if (!target?.dataset || target.dataset[componentPositionDataName]) {
          return;
        }
        // eslint-disable-next-line no-param-reassign
        target.dataset[componentPositionDataName] =
          getComponentPosition(target);
      },
      inCallback: ({ target }) => {
        analyticsEventBus.publish(
          ANALYTICS_EVENTS.pageDesignerComponentImpressionViewed,
          {
            name: getComponentName(target),
            page: getPage(target),
            pageType: getPageType(target),
            position: getDataComponentPosition(target),
            type: getComponentType(target),
          }
        );
      },
    }),
    {
      once: true,
    }
  );
}

const beginTracking = (params = {}) => {
  const mainSelector =
    params?.mainSelector || (params?.id && `#${params.id}`) || '';
  const componentsSelector = `${mainSelector} ${defaultMainComponentsSelector}`;

  // set cache components
  getComponents = makeGetComponents(componentsSelector);

  onDOMContentLoaded(() => trackImpressions(componentsSelector));

  const target = params?.target || document;
  const componentWrapper = target.querySelector('.the-home__modules');
  if (componentWrapper) {
    // The lifespan of the event listener is the whole page visit, so we can
    // omit its removing
    addClickWithoutDraggingEventListener(
      componentWrapper,
      handleComponentClick
    );
  }
};

module.exports = {
  beginTracking,
};
